import { useEffect, useRef, useState } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useSearchParams,
} from 'react-router'
import './index.css'

import normalizeUrl from 'normalize-url'
import { ContentStart } from './components/ContentStart'
import { Email } from './components/Email'
import { GetStarted } from './components/GetStarted'
import { ProtectedRoute } from './components/ProtectedRoute'
import { trackGoogleAdsConversion } from './utils/analytics'
import { getConciergeType, getDwEnv, submitConcierge } from './utils/api'
import { buildDashboardRedirectUrl } from './utils/dashboard'
import { errorMessages, FOUND } from './utils/errorMessages'

import mixpanel from 'mixpanel-browser'
import { SubmissionOverlay } from './components/SubmissionOverlay'

const dwEnv = getDwEnv()
const debug = ['local', 'staging'].includes(dwEnv)
mixpanel.init('23ca402dca3d567f2b6d1249ea2aec95', {
  debug,
  record_sessions_percent: 2,
  record_heatmap_data: true,
  autocapture: {
    pageview: 'full-url',
    click: true,
    input: true,
    scroll: true,
    submit: true,
    capture_text_content: true,
    allow_selectors: ['button'],
  },
})

mixpanel.register({
  dwEnv,
})

export function App() {
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const [isFound, setIsFound] = useState(false)
  const conciergeType = getConciergeType()
  const url = useRef('')
  const host = useRef('')
  const name = useRef('')
  const email = useRef('')
  const product = useRef('free')
  const via = useRef('')
  const primaryValue = useRef('')
  const urlParam = searchParams.get('url')
  const productParam = searchParams.get('product')
  const emailParam = searchParams.get('email')
  const viaParam = searchParams.get('via')

  useEffect(() => {
    if (productParam) product.current = productParam
    if (viaParam) via.current = viaParam

    const isEmailValid = emailParam && isValidEmail(emailParam)
    const normalized = urlParam && normalizeUrl(urlParam)
    const hasValidHostname = normalized && isValidHost(normalized)

    if (isEmailValid) {
      email.current = emailParam!
    }

    if (hasValidHostname) {
      const parsed = new URL(normalized!)
      url.current = parsed.origin
      host.current = parsed.hostname
      primaryValue.current = parsed.origin
    }

    if (primaryValue.current && email.current) {
      handleSubmit(email.current)
      return
    }

    if (primaryValue.current) {
      handleNavigate('email')
      return
    }
  }, [])

  function isValidEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  function isValidHost(url: string): boolean {
    try {
      const { hostname } = new URL(url)
      return /\.\w{2,}$/i.test(hostname)
    } catch {
      return false
    }
  }

  function handleNavigate(path: string) {
    const params = searchParams.toString()
    const hash = window.location.hash
    navigate(params ? `/${path}?${params}${hash}` : `/${path}${hash}`)
    setError('')
  }

  function isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  }

  function redirectToDashboard(conciergeId?: string) {
    if (!conciergeId) {
      const queryString = searchParams.toString()
      navigate(queryString ? `/?${queryString}` : '/')
      return
    }

    const redirectUrl = buildDashboardRedirectUrl(
      conciergeType,
      conciergeId,
      email.current || undefined,
    )
    window.location.assign(redirectUrl)
  }

  function handleRedirect(checkoutUrl: string, conciergeId?: string) {
    if (checkoutUrl) {
      if (isMobileDevice()) {
        window.location.assign(checkoutUrl)
      } else {
        window.open(checkoutUrl)
      }
    }
    redirectToDashboard(conciergeId)
  }

  function handleUrl(value: string) {
    try {
      const normalized = normalizeUrl(value.trim())

      if (!isValidHost(normalized)) {
        setError('Please enter a valid website URL.')
        return
      }

      const urlObj = new URL(normalized)

      url.current = urlObj.origin
      host.current = urlObj.hostname
      primaryValue.current = urlObj.origin

      mixpanel.track('OnboardingURLInserted', { url: url.current })

      if (email.current) {
        handleSubmit(email.current)
        return
      }

      handleNavigate('email')
    } catch (error) {
      setError('Please enter a valid website URL.')
    }
  }

  function handleName(value: string) {
    const trimmedName = value.trim()

    if (!trimmedName) {
      setError('Please enter a concierge name.')
      return
    }

    name.current = trimmedName
    primaryValue.current = trimmedName

    mixpanel.track('OnboardingNameInserted', { name: name.current })

    if (email.current) {
      handleSubmit(email.current)
      return
    }

    handleNavigate('email')
  }

  function handleError(response: Response, data: any): string {
    if (data?.error) return data.error

    const statusCode = response.status.toString()
    return errorMessages[statusCode] || errorMessages.default
  }

  async function handleSubmit(value: string) {
    email.current = value
    setError('')
    setIsFound(false)

    try {
      setLoading(true)
      trackGoogleAdsConversion()
      const response = await submitConcierge(
        conciergeType,
        primaryValue.current,
        email.current,
        product.current,
        window.promotekit_referral,
      )

      if (!response) {
        setError(errorMessages.default)
        return
      }

      const data = await response.json()

      const conciergeId = data?.conciergeId ?? ''

      if (response.status === FOUND) {
        setIsFound(true)
        redirectToDashboard(conciergeId)
        return
      }

      if (!response.ok) {
        const errorMessage = handleError(response, data)
        setError(errorMessage)
        return
      }

      if (debug) console.log(data, response)

      if (conciergeId) {
        mixpanel.identify(conciergeId)
      }

      const trackingData = {
        email: email.current,
        conciergeId,
        product: product.current,
        type: conciergeType,
        ...(conciergeType === 'web'
          ? { url: url.current }
          : { name: name.current }),
        ...(via.current ? { via: via.current } : {}),
      }

      mixpanel.people.set(trackingData)
      mixpanel.track('UserSignedUp', trackingData)

      handleRedirect(data.checkoutUrl, conciergeId)
    } catch (error) {
      console.error('Unexpected error:', error)
      setError(errorMessages.default)
      setLoading(false)
    } finally {
      mixpanel.reset()
    }
  }

  return (
    <div data-testid='app-root'>
      <div className='p-6 relative isolate bg-dw-light-yellow flex flex-col items-center justify-center h-screen overflow-hidden'>
        <div
          aria-hidden='true'
          className='absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80'
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className='relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-dw-yellow-accent to-dw-gold opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]'
          />
        </div>
        <Routes>
          <Route
            path='/'
            element={
              conciergeType === 'web' ? (
                <GetStarted handleUrl={handleUrl} error={error} />
              ) : (
                <ContentStart handleName={handleName} error={error} />
              )
            }
          />

          <Route
            path='/email'
            element={
              <ProtectedRoute
                element={
                  loading ? (
                    <div className='fixed inset-0 z-50 flex items-center justify-center bg-dw-light-yellow px-4'>
                      <SubmissionOverlay isFound={isFound} />
                    </div>
                  ) : (
                    <Email
                      host={host.current}
                      handleNavigate={handleNavigate}
                      handleSubscribe={handleSubmit}
                      error={error}
                    />
                  )
                }
                condition={!!primaryValue.current}
              />
            }
          />
          <Route
            path='/animate'
            element={<SubmissionOverlay isFound={isFound} />}
          />
          <Route
            path='/*'
            element={
              <Navigate
                to={`/?${searchParams.toString()}${window.location.hash}`}
                replace
              />
            }
          />
        </Routes>

        <div
          aria-hidden='true'
          className='absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]'
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className='relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-dw-gold to-dw-yellow-accent opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]'
          />
        </div>
        <footer className='w-full text-center py-4'>
          <p className='text-dw-gold text-sm'>
            © 2025{' '}
            <a
              href='https://digitalwisdom.ai'
              className='underline hover:text-dw-yellow-accent'
              target='_blank'
              rel='noopener noreferrer'
            >
              Digital Wisdom Inc.
            </a>{' '}
            All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  )
}
